import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Dialog, Slide, Box, Chip, TextField, Typography,
  InputAdornment, Checkbox, Tooltip
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import ComplaintForm from "./ComplaintForm";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallback = [
  { complaintId: "CMP001", complainantName: "Andrei Bloom", subject: "Classroom Issue", date: "12/02/2025", status: "Resolved" },
  { complaintId: "CMP002", complainantName: "Sarah Connor", subject: "Facility Problem", date: "15/02/2025", status: "Under Investigation" },
  { complaintId: "CMP003", complainantName: "John Smith", subject: "Teacher Feedback", date: "18/02/2025", status: "Resolved" },
  { complaintId: "CMP004", complainantName: "Emily Davis", subject: "Schedule Conflict", date: "20/02/2025", status: "Under Investigation" },
  { complaintId: "CMP005", complainantName: "Michael Lee", subject: "Material Issue", date: "22/02/2025", status: "Resolved" },
  { complaintId: "CMP006", complainantName: "Lisa Wong", subject: "Transport Problem", date: "25/02/2025", status: "Under Investigation" },
];

const dataColumns = [
  { label: "Complaint ID", field: "complaintId" },
  { label: "Complainant Name", field: "complainantName" },
  { label: "Subject", field: "subject" },
  { label: "Date", field: "date" },
  { label: "Status", field: "status" },
];

const Complaint = () => {
  const [open, setOpen] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadComplaints = useCallback(async () => {
    try {
      const email = sessionStorage.getItem("userEmail") || "";
      const url = email ? `student/complaint/list?email=${encodeURIComponent(email)}` : "student/complaint/list";
      const response = await fetchData(url);
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setRawData(data.length > 0 ? data : fallback);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadComplaints(); }, [loadComplaints]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredRaw = filterRows(rawData, filterText);

  const allSelected = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r.complaintId));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r.complaintId));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRaw.map((r) => r.complaintId)));
  };

  const SelectAllHeader = (
    <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
        sx={{ padding: "2px" }}
      />
    </Tooltip>
  );

  const columns = [{ label: SelectAllHeader, field: "checkbox" }, ...dataColumns];

  const displayRows = filteredRaw.map((row) => ({
    ...row,
    checkbox: (
      <Checkbox
        checked={selectedIds.has(row.complaintId)}
        onChange={() => handleToggleSelect(row.complaintId)}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
        sx={{ padding: "4px" }}
      />
    ),
  }));

  const handleExport = () => exportToCSV(dataColumns, filteredRaw, "complaints.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    button: { background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "14px", textTransform: "none", padding: "8px 20px", borderRadius: "8px" },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
  };

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <Dialog open={open} TransitionComponent={Transition} keepMounted sx={{ "& .MuiPaper-root": { borderRadius: "20px" } }}>
        <ComplaintForm handleClose={() => { setOpen(false); loadComplaints(); }} />
      </Dialog>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Complaint Management
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Complaint Management</h2>
          <Chip label={`${filteredRaw.length} Complaints`} sx={styles.badge} />
        </Box>
        <Button onClick={() => setOpen(true)} style={styles.button}>Add Complaint</Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by name, subject, status..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={columns} rows={displayRows} />
    </div>
  );
};

export default Complaint;
